// src/store/dataSlice.js
import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import {
  loadGeoJSON,
  loadScoredData,
  loadCompetitorData,
  loadOpportunityData,
} from "../services/firebase";
import {
  selectActiveDataPaths,
  selectActiveDisplayConfig,
  selectActiveTierCutoffs,
  selectActiveGeographyType,
} from "./configSlice";

/**
 * Async thunk to load GeoJSON for the active map
 */
export const fetchGeoJSON = createAsyncThunk(
  "data/fetchGeoJSON",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const dataPaths = selectActiveDataPaths(state);

      if (!dataPaths?.geoJson) {
        return rejectWithValue("No GeoJSON path configured");
      }

      const geoJSON = await loadGeoJSON(
        dataPaths.geoJson,
        dataPaths.topoObjectName,
      );
      return geoJSON;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to load scored data for the active map
 */
export const fetchScoredData = createAsyncThunk(
  "data/fetchScoredData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const dataPaths = selectActiveDataPaths(state);

      if (!dataPaths?.scoredData) {
        return rejectWithValue("No scored data path configured");
      }

      const scoredData = await loadScoredData(dataPaths.scoredData);
      return scoredData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to load all data for the active map (GeoJSON + Scored Data)
 */
export const fetchMapData = createAsyncThunk(
  "data/fetchMapData",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.all([
        dispatch(fetchGeoJSON()).unwrap(),
        dispatch(fetchScoredData()).unwrap(),
      ]);

      return {
        geoJSON: results[0],
        scoredData: results[1],
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to load competitor data for the active map.
 * Lazy-loaded — only called when the user first enables the competitors toggle.
 * Caches by competitorsPath so maps sharing the same file don't re-fetch.
 */
export const fetchCompetitorData = createAsyncThunk(
  "data/fetchCompetitorData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const dataPaths = selectActiveDataPaths(state);

      if (!dataPaths?.competitors) {
        return rejectWithValue("No competitors path configured for this map");
      }

      // Check if already loaded for this path (dedup across maps sharing same file)
      const cached = state.data.competitorsByPath[dataPaths.competitors];
      if (cached) {
        return { path: dataPaths.competitors, data: cached };
      }

      const competitorData = await loadCompetitorData(dataPaths.competitors);
      return { path: dataPaths.competitors, data: competitorData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Async thunk to load opportunity data for the active map.
 * Lazy-loaded — only called when the user first enables the opportunities toggle.
 * Caches by opportunitiesPath so maps sharing the same file don't re-fetch.
 */
export const fetchOpportunityData = createAsyncThunk(
  "data/fetchOpportunityData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const dataPaths = selectActiveDataPaths(state);

      if (!dataPaths?.opportunities) {
        return rejectWithValue("No opportunities path configured for this map");
      }

      // Check if already loaded for this path (dedup across maps sharing same file)
      const cached = state.data.opportunitiesByPath[dataPaths.opportunities];
      if (cached) {
        return { path: dataPaths.opportunities, data: cached };
      }

      const opportunityData = await loadOpportunityData(
        dataPaths.opportunities,
      );
      return { path: dataPaths.opportunities, data: opportunityData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  geoJSON: null,
  geoJSONLoading: false,
  geoJSONError: null,

  scoredData: null,
  scoredDataLoading: false,
  scoredDataError: null,

  // Competitor data keyed by competitorsPath for dedup across maps
  competitorsByPath: {},
  competitorsLoading: false,
  competitorsError: null,

  // Opportunity data keyed by opportunitiesPath for dedup across maps
  opportunitiesByPath: {},
  opportunitiesLoading: false,
  opportunitiesError: null,

  loading: false,
  error: null,

  loadedMapId: null,
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    clearData: (state) => {
      state.geoJSON = null;
      state.scoredData = null;
      state.geoJSONError = null;
      state.scoredDataError = null;
      state.competitorsError = null;
      state.opportunitiesError = null;
      state.error = null;
      state.loadedMapId = null;
      // Note: competitorsByPath and opportunitiesByPath are NOT cleared here —
      // they persist across map switches since multiple maps can share the same file.
    },

    clearCompetitorData: (state) => {
      state.competitorsByPath = {};
      state.competitorsError = null;
    },

    clearOpportunityData: (state) => {
      state.opportunitiesByPath = {};
      state.opportunitiesError = null;
    },

    clearError: (state) => {
      state.error = null;
      state.geoJSONError = null;
      state.scoredDataError = null;
      state.competitorsError = null;
      state.opportunitiesError = null;
    },

    setLoadedMapId: (state, action) => {
      state.loadedMapId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // GeoJSON loading
      .addCase(fetchGeoJSON.pending, (state) => {
        state.geoJSONLoading = true;
        state.geoJSONError = null;
      })
      .addCase(fetchGeoJSON.fulfilled, (state, action) => {
        state.geoJSON = action.payload;
        state.geoJSONLoading = false;
      })
      .addCase(fetchGeoJSON.rejected, (state, action) => {
        state.geoJSONLoading = false;
        state.geoJSONError = action.payload;
      })

      // Scored data loading
      .addCase(fetchScoredData.pending, (state) => {
        state.scoredDataLoading = true;
        state.scoredDataError = null;
      })
      .addCase(fetchScoredData.fulfilled, (state, action) => {
        state.scoredData = action.payload;
        state.scoredDataLoading = false;
      })
      .addCase(fetchScoredData.rejected, (state, action) => {
        state.scoredDataLoading = false;
        state.scoredDataError = action.payload;
      })

      // Combined map data loading
      .addCase(fetchMapData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMapData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchMapData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Competitor data loading
      .addCase(fetchCompetitorData.pending, (state) => {
        state.competitorsLoading = true;
        state.competitorsError = null;
      })
      .addCase(fetchCompetitorData.fulfilled, (state, action) => {
        const { path, data } = action.payload;
        state.competitorsByPath[path] = data;
        state.competitorsLoading = false;
      })
      .addCase(fetchCompetitorData.rejected, (state, action) => {
        state.competitorsLoading = false;
        state.competitorsError = action.payload;
      })

      // Opportunity data loading
      .addCase(fetchOpportunityData.pending, (state) => {
        state.opportunitiesLoading = true;
        state.opportunitiesError = null;
      })
      .addCase(fetchOpportunityData.fulfilled, (state, action) => {
        const { path, data } = action.payload;
        state.opportunitiesByPath[path] = data;
        state.opportunitiesLoading = false;
      })
      .addCase(fetchOpportunityData.rejected, (state, action) => {
        state.opportunitiesLoading = false;
        state.opportunitiesError = action.payload;
      });
  },
});

export const {
  clearData,
  clearCompetitorData,
  clearOpportunityData,
  clearError,
  setLoadedMapId,
} = dataSlice.actions;

// ============================================
// BASIC SELECTORS
// ============================================

export const selectGeoJSON = (state) => state.data.geoJSON;
export const selectScoredData = (state) => state.data.scoredData;
export const selectDataLoading = (state) =>
  state.data.loading ||
  state.data.geoJSONLoading ||
  state.data.scoredDataLoading;
export const selectDataError = (state) =>
  state.data.error || state.data.geoJSONError || state.data.scoredDataError;
export const selectLoadedMapId = (state) => state.data.loadedMapId;

// Competitor selectors
export const selectCompetitorsByPath = (state) => state.data.competitorsByPath;
export const selectCompetitorsLoading = (state) =>
  state.data.competitorsLoading;
export const selectCompetitorsError = (state) => state.data.competitorsError;

// Opportunity selectors
export const selectOpportunitiesByPath = (state) =>
  state.data.opportunitiesByPath;
export const selectOpportunitiesLoading = (state) =>
  state.data.opportunitiesLoading;
export const selectOpportunitiesError = (state) =>
  state.data.opportunitiesError;

// ============================================
// MEMOIZED SELECTORS
// ============================================

/**
 * Select the metadata from scored data
 */
export const selectScoredMetadata = createSelector(
  [selectScoredData],
  (scoredData) => scoredData?.metadata || null,
);

/**
 * Select the geographies array from scored data
 */
export const selectGeographies = createSelector(
  [selectScoredData],
  (scoredData) => scoredData?.geographies || [],
);

/**
 * Select geographies as a lookup object keyed by geoId
 */
export const selectGeographiesById = createSelector(
  [selectGeographies],
  (geographies) => {
    const lookup = {};
    geographies.forEach((geo) => {
      // Use geoId as the key
      if (geo.geoId) {
        lookup[geo.geoId] = geo;
      }
    });
    return lookup;
  },
);

/**
 * Select top N geographies by rank
 */
export const selectTopGeographies = createSelector(
  [selectGeographies, (_, n = 10) => n],
  (geographies, n) => {
    return [...geographies]
      .sort((a, b) => (a.rank || 9999) - (b.rank || 9999))
      .slice(0, n);
  },
);

/**
 * Select tier distribution counts
 */
export const selectTierCounts = createSelector(
  [selectGeographies],
  (geographies) => {
    const counts = {};

    geographies.forEach((geo) => {
      const tier = geo.tier;
      const normalizedTier = tier?.toLowerCase()?.trim() || "no data";

      if (!counts[normalizedTier]) {
        counts[normalizedTier] = 0;
      }
      counts[normalizedTier]++;
    });

    return counts;
  },
);

/**
 * Select total geography count
 */
export const selectTotalCount = createSelector(
  [selectGeographies],
  (geographies) => geographies.length,
);

/**
 * Select scored geography count (excluding no data)
 */
export const selectScoredCount = createSelector(
  [selectTierCounts],
  (tierCounts) => {
    const total = Object.values(tierCounts).reduce(
      (sum, count) => sum + count,
      0,
    );
    return total - (tierCounts["no data"] || 0);
  },
);

/**
 * Select score statistics from metadata
 */
export const selectScoreStats = createSelector(
  [selectScoredMetadata],
  (metadata) => metadata?.scores || null,
);

/**
 * Select tier colors as a lookup object
 */
export const selectTierColors = createSelector(
  [(state) => selectActiveTierCutoffs(state)],
  (tierCutoffs) => {
    if (!tierCutoffs) return {};

    const colors = {};
    Object.entries(tierCutoffs).forEach(([key, config]) => {
      // Add multiple formats for flexible lookup
      colors[key.toLowerCase()] = config.color;
      colors[config.label?.toLowerCase()] = config.color;

      // Handle camelCase to space-separated
      const spaced = key
        .replace(/([A-Z])/g, " $1")
        .toLowerCase()
        .trim();
      colors[spaced] = config.color;
    });

    return colors;
  },
);

// ============================================
// COMPETITOR MEMOIZED SELECTORS
// ============================================

/**
 * Select competitor data for the active map.
 * Looks up by the active map's competitorsPath in the competitorsByPath cache.
 * Returns the full competitor JSON object (with metadata + competitors array) or null.
 */
export const selectActiveCompetitorData = createSelector(
  [selectCompetitorsByPath, (state) => selectActiveDataPaths(state)],
  (competitorsByPath, dataPaths) => {
    if (!dataPaths?.competitors) return null;
    return competitorsByPath[dataPaths.competitors] || null;
  },
);

/**
 * Select the competitors array for the active map.
 * Returns an empty array if no competitor data loaded.
 */
export const selectActiveCompetitors = createSelector(
  [selectActiveCompetitorData],
  (competitorData) => competitorData?.competitors || [],
);

/**
 * Select competitors for a specific geography ID.
 * Uses the active map's geography type to determine which field to match
 * (zcta for ZCTA maps, blockGroup for BG maps).
 */
export const selectCompetitorsForGeography = createSelector(
  [
    selectActiveCompetitors,
    (state) => selectActiveGeographyType(state),
    (_, geoId) => geoId,
  ],
  (competitors, geoType, geoId) => {
    if (!competitors.length || !geoId) return [];
    const field = geoType === "BG" ? "blockGroup" : "zcta";
    return competitors.filter((c) => c[field] === geoId);
  },
);

/**
 * Select the count of competitors for the active map.
 */
export const selectActiveCompetitorCount = createSelector(
  [selectActiveCompetitors],
  (competitors) => competitors.length,
);

// ============================================
// OPPORTUNITY MEMOIZED SELECTORS
// ============================================

/**
 * Select opportunity data for the active map.
 * Looks up by the active map's opportunitiesPath in the opportunitiesByPath cache.
 * Returns the full opportunity JSON object (with metadata + opportunities array) or null.
 */
export const selectActiveOpportunityData = createSelector(
  [selectOpportunitiesByPath, (state) => selectActiveDataPaths(state)],
  (opportunitiesByPath, dataPaths) => {
    if (!dataPaths?.opportunities) return null;
    return opportunitiesByPath[dataPaths.opportunities] || null;
  },
);

/**
 * Select the opportunities array for the active map.
 * Returns an empty array if no opportunity data loaded.
 */
export const selectActiveOpportunities = createSelector(
  [selectActiveOpportunityData],
  (opportunityData) => opportunityData?.opportunities || [],
);

/**
 * Select opportunities for a specific geography ID.
 * Uses the active map's geography type to determine which field to match
 * (zcta for ZCTA maps, blockGroup for BG maps).
 */
export const selectOpportunitiesForGeography = createSelector(
  [
    selectActiveOpportunities,
    (state) => selectActiveGeographyType(state),
    (_, geoId) => geoId,
  ],
  (opportunities, geoType, geoId) => {
    if (!opportunities.length || !geoId) return [];
    const field = geoType === "BG" ? "blockGroup" : "zcta";
    return opportunities.filter((o) => o[field] === geoId);
  },
);

/**
 * Select the count of opportunities for the active map.
 */
export const selectActiveOpportunityCount = createSelector(
  [selectActiveOpportunities],
  (opportunities) => opportunities.length,
);

export default dataSlice.reducer;
